import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = "/pt-logo.png?v=2";

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const [tokenValid, setTokenValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setLoading(false);
      return;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        setTokenValid(false);
        setLoading(false);
        return;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      const now = Math.floor(Date.now() / 1000);
      
      if (exp && exp < now) {
        setTokenValid(false);
        toast.error('This reset link has expired. Please request a new one.');
      } else if (payload.type === 'password_reset') {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (e) {
      setTokenValid(false);
    }
    setLoading(false);
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResetting(true);
    
    try {
      const response = await fetch(`${window.location.origin}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetSuccess(true);
        toast.success('Password has been reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#38B6FF] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="reset-password-invalid">
        <div className="max-w-md w-full text-center">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
            <span className="text-2xl font-bold text-white font-['Outfit']">Platinum Touch</span>
          </Link>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Invalid or Expired Link</h1>
            <p className="text-slate-500 text-sm mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/admin">
              <Button className="w-full bg-[#38B6FF] hover:bg-blue-500 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="reset-password-success">
        <div className="max-w-md w-full text-center">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
            <span className="text-2xl font-bold text-white font-['Outfit']">Platinum Touch</span>
          </Link>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Password Reset!</h1>
            <p className="text-slate-500 text-sm mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link to="/admin">
              <Button className="w-full bg-[#38B6FF] hover:bg-blue-500 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="reset-password-form">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
            <span className="text-2xl font-bold text-white font-['Outfit']">Platinum Touch</span>
          </Link>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Reset Password</h1>
          <p className="text-slate-400 mt-2">Enter your new password below</p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleReset} className="space-y-6">
            <div>
              <Label htmlFor="new-password" className="text-slate-700 font-medium">New Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-11 h-12"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-11 h-12"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={resetting}
              className="w-full bg-[#38B6FF] hover:bg-blue-500 text-white h-12 font-semibold"
              data-testid="reset-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link
              to="/admin"
              className="text-sm text-slate-500 hover:text-[#38B6FF] transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
